<script setup lang="ts">
import {EvmTrxDetailInfo} from '~/api/repository'
import type {ViewTrxDetailInfo} from '~/api/service/transaction/[txnHash]/index.service'
import MobileTransactionsPageTransferRow
  from '~/components/@pages/transactions/[txnHash]/mobile/MobileTransactionsPageTransferRow.vue'
import TransactionsPageTransferRow from '~/components/@pages/transactions/[txnHash]/TransactionsPageTransferRow.vue'
import IconButton from '~/components/button/IconButton.vue'
import TransactionStatusChip from '~/components/chip/TransactionStatusChip.vue'
import AddressCopyLink from '~/components/link/AddressCopyLink.vue'
import {UNIT} from '~/environment-variables/unit.const'
import BcLabelContainer from '~/ui/LabelContainer/BcLabelContainer.vue'
import BcStepperItem from '~/ui/Stepper/BcStepperItem.vue'
import TransactionsPageInputData from './TransactionsPageInputData.vue'
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

interface IProps {
  data: ViewTrxDetailInfo
}

const props = defineProps<IProps>()

const {isMobile} = useResponsiveScreen()
const {copy} = useCopy()

const statusIconMap: Record<Partial<EvmTrxDetailInfo.STATTP>, {
  iconColor: string,
  icon: string,
  iconBgColor: string
}> = {
  [EvmTrxDetailInfo.STATTP.SUCCESS]: {
    iconColor: 'text-explorer-teal',
    icon: 'check',
    iconBgColor: 'bg-explorer-teal/10',
  },
  [EvmTrxDetailInfo.STATTP.PENDING]: {
    iconColor: 'text-explorer-yellow',
    icon: 'clock',
    iconBgColor: 'bg-explorer-yellow/10',
  },
  [EvmTrxDetailInfo.STATTP.FAILURE]: {
    iconColor: 'text-explorer-red',
    icon: 'times',
    iconBgColor: 'bg-explorer-red/10',
  }
}

const isPending = computed(() => props.data.STATTP === 'PENDING')
const hasTransfer = computed(() => !isEmpty(props.data.E20TLI) || !isEmpty(props.data.E721TLI) || !isEmpty(props.data.E1155TLI))

const defineComponent = () => isMobile.value ? MobileTransactionsPageTransferRow : TransactionsPageTransferRow
</script>

<template>
  <div
    v-if="data"
    class="transaction-container"
  >
    <div class="transaction-info-cont">
      <div class="flex flex-col gap-y-8 pr-8">
        <BcLabelContainer
          label="Txn Hash"
          gap-y="gap-y-1"
        >
          <template #content>
            <div class="flex">
              <span
                class="txn-hash"
                :class="{'text-explorer-yellow': isPending}"
              >
                {{ data.TRXA }}
              </span>
              <IconButton
                class="ml-1"
                icon="copy"
                @click="copy(data.TRXA)"
              />
            </div>
          </template>
        </BcLabelContainer>
        <BcLabelContainer
          :label="isPending ? 'Time Last Seen' : 'Timestamp'"
          gap-y="gap-y-1"
        >
          <template #content>
            <div class="flex items-center gap-x-2 text-lg">
              <span>{{ data.AG }}</span>
              <TransactionStatusChip :status="data.STATTP"/>
            </div>
            <time
              class="text-base font-normal text-primary/40"
            >
              ({{ data.unixTimeStamp }})
            </time>
          </template>
        </BcLabelContainer>

        <div class="address-container">
          <ol class="address-stepper">
            <BcStepperItem :class="{mobile: isMobile}">
              <template #point>
                <FontAwesomeIcon
                  icon="exchange-alt"
                  class="text-primary/40"
                />
              </template>
              <template #content>
                <BcLabelContainer
                  class="truncate"
                  label="Address"
                  gap-y="gap-y-1"
                >
                  <template #content>
                    <div class="flex items-center">
                      <AddressCopyLink
                        tooltip="click to view address"
                        :address="data.FROMADDRSINFO.ADDR"
                        :type="data.FROMADDRSINFO.ADT"
                        is-detail
                        hide-eoa-icon
                      />
                    </div>
                    <span
                      v-if="data.FROMADDRSINFO.ADT === 'CA' && data.FROMADDRSINFO.NN"
                      class="-mt-1 text-xs font-normal text-primary/40"
                    >({{ data.FROMADDRSINFO.NN }})</span>
                  </template>
                </BcLabelContainer>
              </template>
            </BcStepperItem>
            <BcStepperItem :point-bg-color="statusIconMap[data.STATTP].iconBgColor">
              <template #point>
                <FontAwesomeIcon
                  :class="statusIconMap[data.STATTP].iconColor"
                  :icon="statusIconMap[data.STATTP].icon"
                />
              </template>
              <template #content>
                <BcLabelContainer
                  class="truncate"
                  label="To"
                  gap-y="gap-y-1"
                >
                  <template #content>
                    <div class="flex items-center">
                      <AddressCopyLink
                        class="overflow-visible"
                        tooltip="click to view address"
                        :address="data.TOADDRSINFO.ADDR"
                        :type="data.TOADDRSINFO.ADT"
                        :name="data.TOADDRSINFO.NN"
                        is-detail
                      />
                    </div>
                    <span
                      v-if="data.FROMADDRSINFO.ADT === 'CA' && data.FROMADDRSINFO.NN"
                      class="-mt-1 text-xs font-normal text-primary/40"
                    >({{ data.FROMADDRSINFO.NN }})</span>
                  </template>
                </BcLabelContainer>
              </template>
            </BcStepperItem>
          </ol>
        </div>
      </div>
      <div class="transaction-info-value">
        <div class="title">
          Value
        </div>
        <div class="value">
          <span>{{ data.value }}<span class="ml-1 text-primary/40">{{ UNIT }}</span></span>
        </div>

        <div class="title">
          Txns Fee
        </div>
        <div class="value">
          <span>{{ data.transactionFee }}<span class="ml-1 text-primary/40">{{ UNIT }}</span></span>
        </div>

        <div class="title">
          Gas Price
        </div>
        <div class="value">
          <span>{{ data.gasPrice }}<span class="ml-1 text-primary/40">Gwei</span></span>
        </div>

        <div class="title">
          Gas Used by Transaction
        </div>
        <div class="value">
          <div class="flex items-center gap-x-2">
            <span>{{ data.gasUsed }}</span>
            <Chip :value="`${data.gasUsedPer} %`"/>
          </div>
        </div>

        <div class="title">
          Gas Fees
        </div>
        <div class="value">
          <span>{{ data.burntFees }}<span class="ml-1 text-primary/40">{{ UNIT }}</span></span>
        </div>

        <div class="title">
          Gas Limit
        </div>
        <div class="value">
          <span>{{ data.gasLimit }}</span>
        </div>
      </div>
    </div>

    <template v-if="hasTransfer">
      <hr class="divider">

      <BcLabelContainer
        label="Transaction Logs"
        gap-y="gap-y-1"
      >
        <template #content>
          <div class="transfer-container">
            <component
              :is="defineComponent()"
              v-for="(action, index) in data.transactionAction"
              :key="index"
              :row="action"
            />
          </div>
        </template>
      </BcLabelContainer>
    </template>

    <hr class="divider">

    <BcLabelContainer
      label="Input Data"
      gap-y="gap-y-1"
    >
      <template #content>
        <TransactionsPageInputData :original-input-data="data.IP"/>
      </template>
    </BcLabelContainer>
  </div>
</template>

<style scoped lang="scss">
.transaction-container {
  @apply m-8 text-primary font-bold;

  .transaction-info-cont {
    @apply grid grid-cols-2;

    @include md {
      @apply grid-cols-1;
    }

    .transaction-info-value {
      @apply grid grid-cols-[120px_1fr] gap-y-8;

      @include md {
        @apply border-t border-t-primary/10 pt-8 mt-8 grid-cols-1 gap-y-1;
      }

      @include over_md {
        @apply border-l border-l-primary/10 pl-8;
      }

      .title {
        @apply text-lg text-primary/40;

        &:first-child {
          @apply mt-0;
        }

        @include md {
          @apply mt-4;
        }
      }

      .value {
        @apply text-lg flex;
      }
    }
  }

  .txn-hash {
    @apply flex items-center break-all;

    @include sm {
      @apply text-lg;
    }
  }

  .address-container {
    @apply flex flex-col gap-y-8;

    .address-stepper {
      @apply flex flex-col gap-y-8;

      @include sm {
        @apply gap-y-4;
      }
    }
  }
}

.divider {
  @apply my-8 h-px w-full opacity-10;
}
</style>